import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useOutlet, useLocation } from 'react-router-dom';
import { logout, getCurrentUser, setUserStatus } from '../../store/slices/authSlice';
import { updateUserStatusInList, fetchUsers } from '../../store/slices/userSlice';
import { toggleSidebar, setSidebarOpen, toggleDarkMode, updateDarkModePreference, addNotification, setTriggeredReminder, clearTriggeredReminder, patchTriggeredReminder, setReminderInvite, clearReminderInvite, focusPendingReminders } from '../../store/slices/uiSlice';
import { addMessage, markChannelAsRead, setCurrentChannel, fetchUnreadCounts, fetchLastViewed, fetchMessages, removeMessage, receiveMentionAlert } from '../../store/slices/chatSlice';
import { fetchChannels, fetchDirectMessages, getOrCreateDirectMessage } from '../../store/slices/channelSlice';
import { fetchOrgWorkflowReactions } from '../../store/slices/workflowCatalogSlice';
import { isMessageMentioningUser } from '../../utils/mentionUtils';
import { parseDeliveryFlagsFromPayload } from '../../utils/messageDeliveryStatus';
import { acknowledgeMessageDelivered } from '../../services/messageDeliveryAck';
import { isAttendanceChannel, isMyAttendanceTimelineMessage } from '../../services/attendanceService';
import { isDirectMessageChannelType, isHiddenForGlobalOrgUser, isVisibleSidebarChannel } from '../../utils/channelTypeUtils';
import { notifyTeamLiveStatusChanged } from '../../services/attendanceSessionBus';
import {
  buildPresencePatchFromAttendanceEvent,
  buildPresencePatchFromStatusUpdate,
} from '../../utils/presenceLabelUtils';
import {
  fetchIncomingRequests,
  fetchOutgoingRequests,
  fetchApprovedAccess,
  fetchGrantedAccess,
  receivePanelAccessEvent,
} from '../../store/slices/panelAccessSlice';
import {
  fetchTaskById,
  receiveTaskDelegatedEvent,
  applyLiveTaskToTodayCache,
  upsertLiveTask,
  broadcastTaskUpdated,
} from '../../store/slices/taskSlice';
import { recomputeLiveTrackingAttention, ingestLiveTrackingMessage, loadLiveTracking } from '../../store/slices/liveTrackingSlice';
import { receiveMeetingEvent } from '../../store/slices/meetingNotificationSlice';
import { receiveUserNotification, pushHighlightedToast, dismissHighlightedToastsByKeys } from '../../store/slices/userNotificationSlice';
import { isHighlightedPopupEnabled } from '../../utils/highlightedNotificationPrefs';
import { formatPanelAccessNotification, formatUserNotification, buildMessageHighlightToast, formatReminderTriggeredNotification, formatAttendanceSpokenLine } from '../../utils/notificationMessageUtils';
import { notifyNeedToCallChanged } from '../../utils/suggestedReplies';
import { isMobileLayout } from '../../utils/responsive';
import { consumeSidebarIntent } from '../../utils/sidebarIntent';
import { VOICE_TASK_EVENT, matchPersonBySpokenName } from '../../utils/voiceTaskCommands';
import { startDirectCall } from '../../services/liveKitService';
import { openLiveKitCallWindow } from '../../utils/liveKitCallWindow';
import { getLiveKitRoomName } from '../../utils/meetingUtils';
import Sidebar from './Sidebar';
import { MenuIcon } from './SidebarIcons';
import ChatWindow from './ChatWindow';
import CalendarPanel from './CalendarPanel';
import EventsHomePanel from '../../features/events/components/EventsHomePanel';
import NotesHomePanel from '../../features/notes/components/NotesHomePanel';
import DailyTasksPanel from '../reminders/DailyTasksPanel';
import LiveTrackingPanel from '../liveTracking/LiveTrackingPanel';
import TaskManagementPanel from '../tasks/TaskManagementPanel';
import ReminderTriggeredModal from '../reminders/ReminderTriggeredModal';
import ReminderInviteModal from '../reminders/ReminderInviteModal';
import { markReminderCompleteForToday, snoozeReminder, skipReminderForToday, toggleReminderTask, fetchRemindersForMe, acceptReminder, rejectReminder } from '../reminders/reminderService';
import { formatSnoozeDuration } from '../reminders/SnoozeDropdown';
import WorkflowViewDialog from './WorkflowViewDialog';
import TaskViewDialog from '../tasks/TaskViewDialog';
import ImageViewerModal from './ImageViewerModal';
import { isWorkflowMessage, messageToWorkflowItem } from '../../utils/workflowTaskUtils';
import { WORK_ITEM_SHARE_KIND } from '../../utils/workItemShare';
import { downloadAttachment, openAttachmentFallback } from '../../utils/fileUtils';
import { resolveLocalMessage } from '../../realm/offlineLookups';
import { getTopLevelParentId } from '../../utils/threadUtils';
import websocketService from '../../services/websocketService';
import notificationService from '../../services/notificationService';
import { store } from '../../store';
import { isOwnReactionBroadcast, pickChatTranslationFields, pickSensitiveMessageFields, shouldAnnounceIncomingMessage } from '../../utils/websocketMessageUtils';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { applyWindowTitle } from '../../utils/windowTitle';
import { isRealmEnabled, pullDelta, syncRecentChannelMessagesToRedux, upsertRealmDocs, deleteRealmDoc } from '../../realm';

/** Cursor for GET /api/messages/updates — one poll for all channels. */
const MESSAGE_UPDATES_CURSOR_KEY = 'workloop:messageUpdatesCursor';
const readMessageUpdatesCursor = () => {
  try {
    return localStorage.getItem(MESSAGE_UPDATES_CURSOR_KEY) || null;
  } catch {
    return null;
  }
};
const writeMessageUpdatesCursor = (cursor) => {
  if (!cursor) return;
  try {
    localStorage.setItem(MESSAGE_UPDATES_CURSOR_KEY, cursor);
  } catch {
    // ignore
  }
};

const reminderInviteLaterKey = (userId) => `workloop.reminderInvite.later.${userId || 'anon'}`;
const reminderInviteIdOf = (item) => String(item?.id || item?.reminderId || '');
const notifyRemindersForMeChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('workloop:reminders-for-me-changed'));
};
const readReminderInviteLaterIds = (userId) => {
  try {
    const raw = localStorage.getItem(reminderInviteLaterKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};
const addReminderInviteLaterId = (userId, id) => {
  const reminderId = String(id || '');
  if (!reminderId) return;
  const ids = new Set(readReminderInviteLaterIds(userId));
  ids.add(reminderId);
  try {
    localStorage.setItem(reminderInviteLaterKey(userId), JSON.stringify([...ids]));
  } catch {
    // ignore
  }
};
const pickPendingReminderInvite = (list, laterIds, excludeId) => {
  const skipped = new Set((laterIds || []).map(String));
  if (excludeId) skipped.add(String(excludeId));
  return (Array.isArray(list) ? list : []).find((item) =>
    String(item?.status || '').toUpperCase() === 'PENDING'
    && reminderInviteIdOf(item)
    && !skipped.has(reminderInviteIdOf(item))
  ) || null;
};

const mapPolledMessage = (msg) => ({
  id: msg.id,
  content: msg.content || '',
  ...pickSensitiveMessageFields(msg),
  ...pickChatTranslationFields(msg),
  senderId: msg.senderId,
  senderName: msg.senderName,
  senderProfilePicture: msg.senderProfilePicture || null,
  channelId: msg.channelId,
  type: msg.type,
  attachments: msg.attachments || [],
  reactions: msg.reactions || [],
  customTags: msg.customTags || [],
  replyToMessageId: msg.replyToMessageId || null,
  mentionedUserIds: msg.mentionedUserIds || [],
  ...parseDeliveryFlagsFromPayload(msg),
  isEdited: msg.isEdited || false,
  linkPreview: msg.linkPreview || null,
  meetingInfo: msg.meetingInfo || null,
  appointmentInfo: msg.appointmentInfo || null,
  eventInfo: msg.eventInfo || null,
  attendanceInfo: msg.attendanceInfo || null,
  notificationInfo: msg.notificationInfo || null,
  workItemShare: msg.workItemShare || null,
  linkedMeetingMessageId: msg.linkedMeetingMessageId || null,
  workflowAssignedToUserId: msg.workflowAssignedToUserId ?? null,
  workflowAssignedToName: msg.workflowAssignedToName ?? null,
  workflowDescription: msg.workflowDescription ?? null,
  workflowTitle: msg.workflowTitle ?? null,
  workflowSpaceId: msg.workflowSpaceId ?? null,
  workflowSpaceName: msg.workflowSpaceName ?? null,
  workflowTrackId: msg.workflowTrackId ?? null,
  workflowTrackLink: msg.workflowTrackLink ?? null,
  templateInfo: msg.templateInfo || null,
  completionTimeInMinutes: msg.completionTimeInMinutes ?? null,
  urgentDeadlineAt: msg.urgentDeadlineAt || null,
  createdAt: msg.createdAt,
  updatedAt: msg.updatedAt,
  timestamp: msg.timestamp || msg.createdAt,
});

const ChatApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const location = useLocation();
  const taskPanelOpen = Boolean(outlet) || String(location.pathname || '').startsWith('/chat/tasks');
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useSelector((state) => state.auth);
  const { sidebarOpen, darkMode, triggeredReminder, reminderInvite } = useSelector((state) => state.ui);
  const { currentChannel, unreadCounts } = useSelector((state) => state.chat);
  const people = useSelector((state) => state.users?.users || []);
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);

  const [dailyTasksOpen, setDailyTasksOpen] = useState(() => {
    const saved = localStorage.getItem('dailyTasksOpen');
    // First visit (no saved preference): open panel; returning users keep their choice
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [dailyTasksExpanded, setDailyTasksExpanded] = useState(false);
  const [workloadTargetUserId, setWorkloadTargetUserId] = useState(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [eventsFocusId, setEventsFocusId] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [liveTrackingOpen, setLiveTrackingOpen] = useState(false);

  const [threadOpen, setThreadOpen] = useState(false);
  const [notificationWorkflow, setNotificationWorkflow] = useState(null);
  const [notificationTask, setNotificationTask] = useState(null);
  const [notificationGallery, setNotificationGallery] = useState(null);
  const [inviteActionBusy, setInviteActionBusy] = useState(false);
  const [togglingReminderTask, setTogglingReminderTask] = useState(false);

  useEffect(() => {
    localStorage.setItem('dailyTasksOpen', JSON.stringify(dailyTasksOpen));
  }, [dailyTasksOpen]);

  useEffect(() => {
    if (!currentChannel) return;
    if (isHiddenForGlobalOrgUser(currentChannel, user) || !isVisibleSidebarChannel(currentChannel, user)) {
      dispatch(setCurrentChannel(null));
    }
  }, [user, currentChannel, dispatch]);

  useEffect(() => {
    if (!token) return undefined;
    dispatch(fetchOrgWorkflowReactions());
    return undefined;
  }, [dispatch, token]);

  useEffect(() => {
    const handleOpenDailyTasks = (event) => {
      const userId = event?.detail?.userId || null;
      const focusTasks = Boolean(event?.detail?.focusTasks);
      if (userId && !focusTasks) {
        setWorkloadTargetUserId(userId);
      }
      if (focusTasks) {
        setWorkloadTargetUserId(null);
        setEventsOpen(false);
        setNotesOpen(false);
      }
      setCalendarOpen(false);
      setNotesOpen(false);
      setLiveTrackingOpen(false);
      setDailyTasksOpen(true);
      if (userId && !focusTasks) {
        setDailyTasksExpanded(false);
        return;
      }
      setDailyTasksExpanded(true);
      if (focusTasks) {
        window.__workloopFocusTodayTasks = true;
        if (window.location.pathname !== '/chat' && !window.location.hash.startsWith('#/chat')) {
          navigate('/chat');
        } else if (window.location.pathname.startsWith('/chat/') || window.location.hash.startsWith('#/chat/')) {
          navigate('/chat');
        }
        window.dispatchEvent(new CustomEvent('workloop:focus-today-tasks'));
      }
    };
    const handleOpenCalendar = () => {
      setDailyTasksOpen(false);
      setEventsOpen(false);
      setNotesOpen(false);
      setLiveTrackingOpen(false);
      setCalendarOpen(true);
    };
    const consumePendingTodayTasks = () => {
      try {
        if (sessionStorage.getItem('workloop.pendingTodayTasks') !== '1') return;
        sessionStorage.removeItem('workloop.pendingTodayTasks');
      } catch {
        return;
      }
      handleOpenDailyTasks({ detail: { focusTasks: true, extended: true } });
    };
    consumePendingTodayTasks();
    window.addEventListener('workloop:open-daily-tasks', handleOpenDailyTasks);
    window.addEventListener('workloop:open-calendar', handleOpenCalendar);
    const handleOpenWorkflow = (event) => {
      const item = event?.detail;
      if (!item?.messageId) return;
      setNotificationTask(null);
      setNotificationWorkflow(item);
    };
    window.addEventListener('workloop:open-workflow', handleOpenWorkflow);
    const handleOpenTask = (event) => {
      const task = event?.detail;
      if (!task?.id) return;
      setNotificationWorkflow(null);
      setNotificationTask(task);
    };
    window.addEventListener('workloop:open-task', handleOpenTask);
    const handleOpenChannel = (event) => {
      const channel = event?.detail?.channel;
      if (!channel?.id) return;
      dispatch(setCurrentChannel(channel));
      setNotesOpen(false);
      setEventsOpen(false);
      setCalendarOpen(false);
      setDailyTasksExpanded(false);
      setLiveTrackingOpen(false);
      if (isMobileLayout()) {
        setDailyTasksOpen(false);
        dispatch(setSidebarOpen(false));
      }
      if (window.location.pathname !== '/chat' && !window.location.hash.startsWith('#/chat')) {
        navigate('/chat');
      }
    };
    window.addEventListener('workloop:open-channel', handleOpenChannel);
    const handleOpenLiveTracking = () => {
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setCalendarOpen(false);
      setEventsOpen(false);
      setNotesOpen(false);
      setLiveTrackingOpen(true);
    };
    window.addEventListener('workloop:open-live-tracking', handleOpenLiveTracking);
    const handleOpenNotes = () => {
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setCalendarOpen(false);
      setEventsOpen(false);
      setLiveTrackingOpen(false);
      setNotesOpen(true);
      if (isMobileLayout()) {
        dispatch(setSidebarOpen(false));
      }
    };
    window.addEventListener('workloop:open-notes', handleOpenNotes);
    window.addEventListener('workloop:open-call-reminders', handleOpenNotes);
    const handleOpenEvent = (event) => {
      const eventId = event?.detail?.eventId;
      if (!eventId) return;
      setNotificationTask(null);
      setNotificationWorkflow(null);
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setCalendarOpen(false);
      setNotesOpen(false);
      setLiveTrackingOpen(false);
      setEventsFocusId(eventId);
      setEventsOpen(true);
      if (isMobileLayout()) {
        dispatch(setSidebarOpen(false));
      }
    };
    window.addEventListener('workloop:open-event', handleOpenEvent);
    const handleEventsChanged = (event) => {
      if (!event?.detail?.revealList) return;
      const eventId = event?.detail?.event?.id;
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setCalendarOpen(false);
      setNotesOpen(false);
      setLiveTrackingOpen(false);
      if (eventId) setEventsFocusId(eventId);
      setEventsOpen(true);
      if (isMobileLayout()) {
        dispatch(setSidebarOpen(false));
      }
    };
    window.addEventListener('workloop:events-changed', handleEventsChanged);
    return () => {
      window.removeEventListener('workloop:open-daily-tasks', handleOpenDailyTasks);
      window.removeEventListener('workloop:open-calendar', handleOpenCalendar);
      window.removeEventListener('workloop:open-live-tracking', handleOpenLiveTracking);
      window.removeEventListener('workloop:open-notes', handleOpenNotes);
      window.removeEventListener('workloop:open-call-reminders', handleOpenNotes);
      window.removeEventListener('workloop:open-event', handleOpenEvent);
      window.removeEventListener('workloop:events-changed', handleEventsChanged);
      window.removeEventListener('workloop:open-workflow', handleOpenWorkflow);
      window.removeEventListener('workloop:open-task', handleOpenTask);
      window.removeEventListener('workloop:open-channel', handleOpenChannel);
    };
  }, []);

  useEffect(() => {
    const onVoice = async (event) => {
      if (event?.detail?.action !== 'callUser') return;
      const spoken = String(event.detail?.value || '').trim();
      if (!spoken) {
        dispatch(addNotification({ type: 'info', message: 'Say a name after call' }));
        return;
      }
      try {
        let roster = people;
        if (!roster.length) {
          roster = await dispatch(fetchUsers()).unwrap();
        }
        const matched = matchPersonBySpokenName(spoken, roster);
        if (!matched?.id) {
          dispatch(addNotification({ type: 'info', message: `Could not match “${spoken}” to a teammate` }));
          return;
        }
        const channel = await dispatch(getOrCreateDirectMessage(matched.id)).unwrap();
        const channelId = channel?.id || channel?._id;
        if (!channelId) throw new Error('Could not open a direct chat');
        dispatch(setCurrentChannel(channel));
        const label = [matched.firstName, matched.lastName].filter(Boolean).join(' ') || matched.username || spoken;
        const message = await startDirectCall(channelId, `Call ${label}`);
        const opened = openLiveKitCallWindow({
          channelId,
          messageId: message.id,
          roomName: getLiveKitRoomName(message.meetingInfo),
          title: message.meetingInfo?.label || `Call ${label}`,
        });
        if (!opened) {
          dispatch(addNotification({ type: 'error', message: 'Could not open the call window' }));
        }
      } catch (err) {
        dispatch(addNotification({
          type: 'error',
          message: err?.response?.data?.error || err?.message || 'Could not start call',
        }));
      }
    };
    window.addEventListener(VOICE_TASK_EVENT, onVoice);
    return () => window.removeEventListener(VOICE_TASK_EVENT, onVoice);
  }, [dispatch, people]);

  const splitPanelOpen = dailyTasksOpen || threadOpen;

  // Personal calendar (sidebar) is a full-screen overlay — lock body scroll while open
  useEffect(() => {
    if (!calendarOpen && !eventsOpen && !notesOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (notesOpen) setNotesOpen(false);
      else if (eventsOpen) setEventsOpen(false);
      else setCalendarOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [calendarOpen, eventsOpen, notesOpen]);

  // Deep-link params for notification / search navigation
  const messageIdToScroll = searchParams.get('messageId');
  const threadIdToOpen = searchParams.get('threadId');
  const channelIdToOpen = searchParams.get('channelId');
  const workflowShareId = searchParams.get('workflow');
  const taskShareId = searchParams.get('task');

  useEffect(() => {
    const openSharedWorkflow = async (messageId) => {
      try {
        let found = await resolveLocalMessage(messageId, store.getState().chat?.messages);
        if (!found) {
          const authToken = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/messages/${messageId}`, {
            headers: { Authorization: authToken ? `Bearer ${authToken}` : '' },
          });
          found = response.data;
        }
        const item = messageToWorkflowItem(found);
        if (!item?.messageId) throw new Error('Could not open workflow');
        setNotificationTask(null);
        setNotificationWorkflow(item);
      } catch (err) {
        dispatch(addNotification({
          type: 'error',
          message: typeof err === 'string' ? err : (err?.message || 'Could not open workflow'),
        }));
      }
    };

    const openSharedTask = async (taskId) => {
      try {
        const task = await dispatch(fetchTaskById(taskId)).unwrap();
        if (!task?.id) throw new Error('Could not open task');
        setNotificationWorkflow(null);
        setNotificationTask(task);
      } catch (err) {
        dispatch(addNotification({
          type: 'error',
          message: typeof err === 'string' ? err : (err?.message || 'Could not open task'),
        }));
      }
    };

    const handleOpenSharedItem = (event) => {
      const share = event?.detail || {};
      if (share.kind === WORK_ITEM_SHARE_KIND.TASK && share.itemId) {
        openSharedTask(share.itemId);
        return;
      }
      if (share.itemId) openSharedWorkflow(share.itemId);
    };

    window.addEventListener('workloop:open-shared-item', handleOpenSharedItem);
    return () => window.removeEventListener('workloop:open-shared-item', handleOpenSharedItem);
  }, [dispatch]);

  useEffect(() => {
    if (!workflowShareId && !taskShareId) return undefined;
    let cancelled = false;
    const run = async () => {
      if (taskShareId) {
        try {
          const task = await dispatch(fetchTaskById(taskShareId)).unwrap();
          if (!cancelled && task?.id) {
            setNotificationWorkflow(null);
            setNotificationTask(task);
          }
        } catch (err) {
          if (!cancelled) {
            dispatch(addNotification({
              type: 'error',
              message: typeof err === 'string' ? err : 'Could not open task',
            }));
          }
        }
      } else if (workflowShareId) {
        try {
          let found = await resolveLocalMessage(workflowShareId, store.getState().chat?.messages);
          if (!found) {
            const authToken = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/messages/${workflowShareId}`, {
              headers: { Authorization: authToken ? `Bearer ${authToken}` : '' },
            });
            found = response.data;
          }
          const item = messageToWorkflowItem(found);
          if (!cancelled && item?.messageId) {
            setNotificationTask(null);
            setNotificationWorkflow(item);
          }
        } catch (err) {
          if (!cancelled) {
            dispatch(addNotification({
              type: 'error',
              message: typeof err === 'string' ? err : 'Could not open workflow',
            }));
          }
        }
      }
      if (!cancelled) {
        setSearchParams((prev) => {
          if (!prev.get('workflow') && !prev.get('task')) return prev;
          const next = new URLSearchParams(prev);
          next.delete('workflow');
          next.delete('task');
          return next;
        }, { replace: true });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [workflowShareId, taskShareId, dispatch, setSearchParams]);

  useEffect(() => {
    if (!channelIdToOpen) return;
    if (String(currentChannel?.id) === String(channelIdToOpen)) return;
    const allChannels = [...channels, ...directMessages];
    const target = allChannels.find((ch) => String(ch.id) === String(channelIdToOpen));
    if (!target) return;
    dispatch(setCurrentChannel(target));
    setDailyTasksExpanded(false);
    setDailyTasksOpen(false);
    setLiveTrackingOpen(false);
  }, [channelIdToOpen, currentChannel?.id, channels, directMessages, dispatch]);

  useEffect(() => {
    if (!taskPanelOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') navigate('/chat');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [taskPanelOpen, navigate]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      if (searchParams.get('workflow') || searchParams.get('task') || searchParams.get('messageId') || searchParams.get('channelId')) return;
      navigate('/admin', { replace: true });
    }
  }, [user, navigate, searchParams]);

  const isSuperAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    if (isSuperAdmin || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchRemindersForMe();
        if (cancelled) return;
        const next = pickPendingReminderInvite(data, readReminderInviteLaterIds(user.id));
        if (next) dispatch(setReminderInvite(next));
      } catch {
        // Keep chat usable if for-me fetch fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isSuperAdmin, user?.id]);

  useEffect(() => {
    if (isSuperAdmin) return;
    
    // Request notification permission when app loads
    notificationService.requestPermission();

    // Refresh user data periodically to get updated permissions (e.g., announcement access)
    const refreshUserData = () => {
      if (token) {
        dispatch(getCurrentUser());
      }
    };

    // Refresh immediately on mount
    refreshUserData();

    // Then refresh every 5 minutes to keep permissions up to date
    const refreshInterval = setInterval(refreshUserData, 5 * 60 * 1000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [dispatch, token, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) return;

    const catchUpOpenChannel = () => {
      const channelId = store.getState()?.chat?.currentChannel?.id;
      if (!channelId) return;
      dispatch(fetchMessages({ channelId, page: 0, size: 30, skipRealm: true }));
    };
    
    // Connect to WebSocket when component mounts
    if (token && user) {
      // Only connect if not already connected
      if (!websocketService.isConnected()) {
        console.log('🔌 Initializing WebSocket connection...');
        websocketService.connect(token);
      }

      const handleStatusUpdate = (data) => {
        if (data.type === 'STATUS_UPDATE') {
          const status = typeof data.status === 'object'
            ? (data.status?.name || data.status?.status || data.status)
            : data.status;
          const onBreak = data.onBreak === true;
          dispatch(updateUserStatusInList({
            userId: data.userId,
            status,
            workLocation: data.workLocation || null,
            statusMessage: data.statusMessage ?? null,
            statusSource: data.statusSource,
            onBreak,
          }));
          if (user?.id && String(data.userId) === String(user.id)) {
            dispatch(setUserStatus({
              status,
              workLocation: data.workLocation,
              statusMessage: data.statusMessage ?? null,
              statusSource: data.statusSource || null,
              onBreak,
            }));
          }
          const livePatch = buildPresencePatchFromStatusUpdate({
            ...data,
            status,
            onBreak,
          });
          if (livePatch) {
            notifyTeamLiveStatusChanged(livePatch);
          }
        }
      };

      const handleReminderTrigger = (data) => {
        const type = (data && data.type && String(data.type)).toUpperCase();
        if (data && type === 'REMINDER') {
          console.log('🔔 Reminder received via WebSocket:', data);
          const payload = {
            title: data.title ?? 'Reminder',
            description: data.description ?? '',
            id: data.id || data.reminderId,
            reminderId: data.reminderId || data.id,
            timezone: data.timezone,
            time: data.time,
            triggeredAt: data.triggeredAt,
            items: Array.isArray(data.items) ? data.items : undefined,
          };
          dispatch(setTriggeredReminder(payload));
          const reminderSpeech = formatReminderTriggeredNotification();
          notificationService.showNotification(reminderSpeech.title, {
            body: reminderSpeech.body,
            spoken: reminderSpeech.line,
            tag: `reminder-trigger-${payload.id || payload.time || 'now'}`,
          });
          return;
        }
        if (data && type === 'REMINDER_INVITE') {
          const inviteId = reminderInviteIdOf(data);
          if (inviteId && readReminderInviteLaterIds(user?.id).includes(inviteId)) {
            notifyRemindersForMeChanged();
            return;
          }
          dispatch(setReminderInvite(data));
          notifyRemindersForMeChanged();
          return;
        }
        if (data && (type === 'REMINDER_ACCEPTED' || type === 'REMINDER_REJECTED')) {
          notifyRemindersForMeChanged();
          window.dispatchEvent(new CustomEvent('workloop:reminders-flushed'));
        }
      };

      const handleMessageAck = (data) => {
        if (!data?.id || !data.channelId) return;
        const frameType = String(data.type || '').toUpperCase();
        if (frameType === 'ERROR' || frameType === 'TYPING' || frameType === 'USER_JOINED' || frameType === 'MESSAGE_DELETED') {
          return;
        }
        const state = store.getState();
        const currentUserId = state.auth.user?.id;
        const currentUser = state.auth.user;
        const currentChannelId = state.chat.currentChannel?.id;
        const allChannels = [...(state.channels?.channels || []), ...(state.channels?.directMessages || [])];
        const knownChannel = allChannels.some((c) => String(c?.id) === String(data.channelId));
        const fromSelf = String(data.senderId) === String(currentUserId);
        if (!knownChannel && !fromSelf) {
          dispatch(fetchChannels());
          dispatch(fetchDirectMessages());
        }
        const incoming = {
          ...data,
          tempId: data.clientMessageId || data.tempId,
          clientMessageId: data.clientMessageId || data.tempId || null,
          ...parseDeliveryFlagsFromPayload(data),
        };
        const existingMessage = (state.chat.messages[data.channelId] || []).find((m) => m.id === data.id);
        dispatch(addMessage({
          channelId: data.channelId,
          message: incoming,
          currentUserId,
          skipUnreadCountIncrement: fromSelf && String(incoming.templateInfo?.templateType || '').toUpperCase() !== 'SEND_USING_API',
        }));
        const channel = allChannels.find((c) => String(c?.id) === String(data.channelId));
        // Unknown channel is usually a brand-new DM the peer has not listed yet.
        const isDirectMessage = channel
          ? isDirectMessageChannelType(channel)
          : !knownChannel;
        if (!fromSelf && currentUserId) {
          acknowledgeMessageDelivered(incoming, currentUserId);
        }
        if (fromSelf && String(incoming.templateInfo?.templateType || '').toUpperCase() !== 'SEND_USING_API') return;
        if (!shouldAnnounceIncomingMessage({
          existingMessage,
          incoming,
          currentUserId,
          currentChannelId,
          tabVisible: notificationService.isTabVisibleNow(),
          windowFocused: notificationService.isWindowFocusedNow(),
        })) {
          return;
        }
        const channelName = channel?.name || data.channelName || 'Channel';
        notificationService.showMessageNotification(
          incoming,
          channelName,
          isDirectMessage,
          currentChannelId,
          currentUser
        );
        if (isHighlightedPopupEnabled()) {
          dispatch(pushHighlightedToast(buildMessageHighlightToast(incoming, {
            eventType: 'MESSAGE',
            channelName,
            isDirectMessage,
          })));
        }
      };

      const handlePanelAccessEvent = (data) => {
        if (!data?.type || !data?.request || !user?.id) return;
        const eventType = String(data.type).toUpperCase();
        if (!eventType.startsWith('PANEL_ACCESS_')) return;

        console.log('🔔 Panel access event received via WebSocket:', data);
        dispatch(receivePanelAccessEvent({
          type: eventType,
          request: data.request,
          currentUserId: user.id,
        }));
        dispatch(fetchIncomingRequests());
        dispatch(fetchOutgoingRequests());
        dispatch(fetchApprovedAccess());
        dispatch(fetchGrantedAccess());

        const formatted = formatPanelAccessNotification({
          eventType,
          request: data.request,
        });
        notificationService.showNotification(formatted.title, {
          body: formatted.body,
          tag: `panel-access-${data.request.id || eventType}`,
        });
        if (isHighlightedPopupEnabled()) {
          dispatch(pushHighlightedToast({
            eventType,
            title: formatted.title,
            body: formatted.body,
            channelId: data.request?.channelId || data.request?.id,
          }));
        }
      };

      const handleMeetingEvent = (data) => {
        if (!data?.type || !data?.appointmentId) return;
        const eventType = String(data.type).toUpperCase();
        if (!eventType.startsWith('MEETING_')) return;

        console.log('🔔 Meeting event received via WebSocket:', data);
        dispatch(receiveMeetingEvent(data));

        const title = data.notificationTitle
          || (eventType === 'MEETING_REMINDER'
            ? 'Meeting in 15 minutes'
            : eventType === 'MEETING_CANCELLED'
              ? 'Meeting cancelled'
              : eventType === 'MEETING_UPDATED'
                ? 'Meeting updated'
                : 'Meeting scheduled');
        const body = data.notificationBody
          || `${data.title || 'Meeting'}${data.date ? ` · ${data.date}` : ''}${data.startTime ? ` ${data.startTime}` : ''}`;

        notificationService.showNotification(title, {
          body,
          tag: `meeting-${eventType}-${data.appointmentId}`,
        });

        if (isHighlightedPopupEnabled()) {
          dispatch(pushHighlightedToast({
            ...data,
            eventType,
            title,
            body,
            ticketTitle: data.title,
            sourceChannelId: data.channelId,
            sourceMessageId: data.chatMessageId,
          }));
        }
      };

      const handleAttendanceShareEvent = (data) => {
        if (!data || String(data.type || '').toUpperCase() !== 'ATTENDANCE_EVENT') return;
        const livePatch = buildPresencePatchFromAttendanceEvent(data);
        const eventUserId = data.eventUserId || data.senderId;
        // Keep DM/sidebar presence in sync even if STATUS_UPDATE is delayed/missed.
        if (livePatch?.patch && eventUserId) {
          const eventType = String(data.attendanceEventType || '').toUpperCase();
          if (eventType === 'BREAK_START' || eventType === 'BREAK_END' || data.onBreak != null) {
            dispatch(updateUserStatusInList({
              userId: eventUserId,
              status: livePatch.patch.presenceStatus,
              statusMessage: livePatch.patch.statusMessage ?? null,
              statusSource: 'ATTENDANCE',
              onBreak: livePatch.patch.onBreak === true,
            }));
          }
        }
        notifyTeamLiveStatusChanged(
          livePatch || { userId: eventUserId }
        );
        if (user?.id && data.senderId && String(data.senderId) === String(user.id)) return;
        const name = data.senderName || 'Someone';
        const eventType = String(data.attendanceEventType || '').toUpperCase();
        const spoken = formatAttendanceSpokenLine({
          name,
          eventType,
          reason: data.breakReason || data.statusMessage,
          spoken: data.spoken,
          notificationTitle: data.notificationTitle,
        });
        notificationService.showNotification(spoken, {
          body: '',
          spoken,
          tag: `attendance-${data.messageId || data.senderId || 'event'}`,
        });
        if (isHighlightedPopupEnabled()) {
          dispatch(pushHighlightedToast({
            eventType: 'ATTENDANCE_EVENT',
            title: spoken,
            body: spoken,
            actorName: '',
            senderId: data.senderId,
            sourceChannelId: data.channelId,
            sourceMessageId: data.messageId,
          }));
        }
      };

      const subscribeAttendanceTopics = () => {
        if (!user?.id) return;
        const attendanceTopic = '/topic/attendance/' + user.id;
        websocketService.subscribeToTopic(attendanceTopic, handleAttendanceShareEvent);
      };

      const handleTaskLiveEvent = (data) => {
        const eventType = String(data?.type || '').toUpperCase();
        const task = data?.task;
        const taskId = task?.id || data?.taskId;
        if (task?.id) {
          if (eventType === 'TASK_DELETED') {
            deleteRealmDoc('Task', task.id).catch(() => {});
            broadcastTaskUpdated(task, 'deleted');
            dispatch(applyLiveTaskToTodayCache({ task, eventType: 'TASK_DELETED' }));
          } else {
            upsertRealmDocs('Task', [task]).catch(() => {});
            broadcastTaskUpdated(task, eventType === 'TASK_CREATED' ? 'created' : 'updated');
            dispatch(upsertLiveTask(task));
            dispatch(applyLiveTaskToTodayCache({ task, eventType }));
            dispatch(recomputeLiveTrackingAttention());
          }
        }
        if (eventType !== 'TASK_DELEGATED' || !taskId) return;
        dispatch(receiveTaskDelegatedEvent({
          task: task?.id ? task : { ...task, id: taskId },
          delegatorName: data.delegatorName,
          assignmentKind: data.assignmentKind,
        }));
      };

      const handleUserNotificationEvent = (data) => {
        if (!data || String(data.type || '').toUpperCase() !== 'USER_NOTIFICATION') return;
        if (user?.id && data.actorUserId && String(data.actorUserId) === String(user.id)) return;
        const eventType = String(data.eventType || '').toUpperCase();
        if (eventType === 'CALL_ME_BACK' || eventType === 'CALL_THIS_PERSON' || eventType === 'NEED_TO_CALL_NUDGE') {
          notifyNeedToCallChanged();
        }
        if (eventType === 'EVENT_PUBLISHED' || String(data.type || '').toUpperCase() === 'EVENT_INVITE') {
          window.dispatchEvent(new CustomEvent('workloop:event-invite', { detail: data }));
        } else if (eventType === 'TASK_ASSIGNED' && data.taskId) {
          dispatch(receiveTaskDelegatedEvent({
            task: { id: data.taskId, title: data.ticketTitle || data.title || data.body },
            delegatorName: data.actorName,
            assignmentKind: 'DELEGATED',
          }));
        } else if (isHighlightedPopupEnabled()) {
          dispatch(pushHighlightedToast(data));
        }
        dispatch(receiveUserNotification(data));
        const formatted = formatUserNotification(data);
        notificationService.showNotification(formatted.title, {
          body: formatted.body,
          spoken: formatted.spoken || formatted.line,
          tag: `user-notification-${data.eventType || 'event'}-${data.sourceMessageId || data.taskId || Date.now()}`,
        });
      };

      const subscribeTaskTopics = () => {
        websocketService.subscribeToTasks(handleTaskLiveEvent);
        if (user?.id) {
          websocketService.subscribeToTopic('/topic/tasks/' + user.id, handleTaskLiveEvent);
        }
      };

      const subscribeNeedToCallTopics = () => {
        if (!user?.id) return;
        websocketService.subscribeToTopic('/topic/need-to-call/' + user.id, (data) => {
          if (data && String(data.type || '').toUpperCase() === 'NEED_TO_CALL_UPDATED') {
            notifyNeedToCallChanged();
          }
        });
      };

      const subscribeUserNotificationTopics = () => {
        if (!user?.id) return;
        const notificationsTopic = '/topic/notifications/' + user.id;
        websocketService.subscribeToTopic(notificationsTopic, handleUserNotificationEvent);
      };

      const subscribePanelAccessTopics = () => {
        websocketService.subscribeToPanelAccess(handlePanelAccessEvent);
        if (user?.id) {
          const panelAccessTopic = '/topic/panel-access/' + user.id;
          console.log('📡 Subscribing to panel-access topic:', panelAccessTopic);
          websocketService.subscribeToTopic(panelAccessTopic, handlePanelAccessEvent);
        }
      };

      const subscribeMeetingTopics = () => {
        websocketService.subscribeToMeetings(handleMeetingEvent);
        if (user?.id) {
          const meetingTopic = '/topic/meetings/' + user.id;
          console.log('📡 Subscribing to meetings topic:', meetingTopic);
          websocketService.subscribeToTopic(meetingTopic, handleMeetingEvent);
        }
      };

      websocketService.setOnConnect(() => {
        console.log('✅ WebSocket connected successfully');
        // Retry any pending subscriptions immediately after connection
        setTimeout(() => {
          websocketService.retryPendingSubscriptions();
        }, 200);
        websocketService.subscribeToTopic('/topic/user-status', handleStatusUpdate);
        websocketService.subscribeToReminders(handleReminderTrigger);
        websocketService.subscribeToMessageAcks(handleMessageAck);
        subscribePanelAccessTopics();
        subscribeMeetingTopics();
        subscribeTaskTopics();
        subscribeAttendanceTopics();
        subscribeUserNotificationTopics();
        subscribeNeedToCallTopics();
        // Subscribe to user-specific reminder topic (reliable route; backend sends here too)
        if (user?.id) {
          websocketService.subscribeToTopic('/topic/messages/' + user.id, handleMessageAck);
          const reminderTopic = '/topic/reminders/' + user.id;
          console.log('📡 Subscribing to reminder topic:', reminderTopic);
          websocketService.subscribeToTopic(reminderTopic, handleReminderTrigger);
        }
        dispatch(fetchIncomingRequests());
        dispatch(fetchOutgoingRequests());
        dispatch(fetchApprovedAccess());
        dispatch(fetchGrantedAccess());
      });

      // Subscribe immediately if already connected
      if (websocketService.isConnected()) {
        websocketService.subscribeToTopic('/topic/user-status', handleStatusUpdate);
        websocketService.subscribeToReminders(handleReminderTrigger);
        websocketService.subscribeToMessageAcks(handleMessageAck);
        subscribePanelAccessTopics();
        subscribeMeetingTopics();
        subscribeTaskTopics();
        subscribeAttendanceTopics();
        subscribeUserNotificationTopics();
        subscribeNeedToCallTopics();
        if (user?.id) {
          websocketService.subscribeToTopic('/topic/messages/' + user.id, handleMessageAck);
          const reminderTopic = '/topic/reminders/' + user.id;
          console.log('📡 Subscribing to reminder topic (already connected):', reminderTopic);
          websocketService.subscribeToTopic(reminderTopic, handleReminderTrigger);
        }
        dispatch(fetchIncomingRequests());
        dispatch(fetchOutgoingRequests());
        dispatch(fetchApprovedAccess());
        dispatch(fetchGrantedAccess());
      }

      window.addEventListener('workloop:ws-reconnected', catchUpOpenChannel);

      websocketService.setOnDisconnect(() => {
        console.log('⚠️ WebSocket disconnected');
        // Attempt to reconnect after a delay
        setTimeout(() => {
          const latestToken = localStorage.getItem('token');
          if (latestToken && !websocketService.isConnected() && !websocketService.isTabSessionBlocked()) {
            console.log('🔄 Attempting to reconnect WebSocket...');
            websocketService.connect(latestToken);
          }
        }, 3000);
      });

      websocketService.setOnError((error) => {
        console.error('❌ WebSocket error:', error);
      });
    }

    // Unsubscribe on unmount
    return () => {
      window.removeEventListener('workloop:ws-reconnected', catchUpOpenChannel);
      websocketService.unsubscribeFromTopic('/topic/user-status');
      websocketService.unsubscribeFromReminders();
      websocketService.unsubscribeFromMessageAcks();
      websocketService.unsubscribeFromPanelAccess();
      websocketService.unsubscribeFromMeetings();
      if (user?.id) {
        websocketService.unsubscribeFromTopic('/topic/reminders/' + user.id);
        websocketService.unsubscribeFromTopic('/topic/panel-access/' + user.id);
        websocketService.unsubscribeFromTopic('/topic/meetings/' + user.id);
        websocketService.unsubscribeFromTopic('/topic/attendance/' + user.id);
      }
    };
  }, [token, user?.id, user, dispatch, isSuperAdmin]);

  // Subscribe to all channels for unread count tracking when channels are loaded
  useEffect(() => {
    // Do not wait for WS connect — subscribeToChannel queues until STOMP is ready.
    // Waiting broke Electron: Realm fills channels before the socket connects.
    if (!channels.length && !directMessages.length) {
      console.log('⏳ Waiting for channels before WebSocket subscriptions...', {
        channelsCount: channels.length,
        directMessagesCount: directMessages.length,
      });
      return undefined;
    }

    // Keep track of active subscriptions to clean them up
    const activeCallbacks = new Map(); // channelId -> callback function

    const runSubscribe = () => {
      activeCallbacks.forEach((callback, channelId) => {
        websocketService.unsubscribeFromChannel(channelId, callback);
      });
      activeCallbacks.clear();
      const currentState = store.getState();
      const currentChannelFromStore = currentState.chat.currentChannel;
      let allChannels = [...channels, ...directMessages];
      
      // Include current channel if not in list (e.g. just-created self-chat)
      if (currentChannelFromStore?.id && !allChannels.some((c) => c?.id === currentChannelFromStore.id)) {
        allChannels = [...allChannels, currentChannelFromStore];
      }
      
      const currentUser = currentState.auth.user;
      const currentUserId = currentUser?.id;

      console.log('📡 Setting up subscriptions for unread tracking:', {
        totalChannels: allChannels.length,
        currentUserId: currentUserId,
        connected: websocketService.isConnected(),
      });

      // Subscribe to ALL channels for unread count tracking
      allChannels.forEach((channel) => {
        if (channel?.id) {
          const callback = (data) => {
            // Handle MESSAGE_DELETED events for all channels
            if (data.type === 'MESSAGE_DELETED' && data.messageId && data.channelId) {
              console.log('🗑️ [WebSocket] Message deleted event received:', {
                messageId: data.messageId,
                channelId: data.channelId,
                subscribedChannelId: channel.id
              });
              dispatch(removeMessage({ channelId: data.channelId, messageId: data.messageId }));
              return;
            }

            if (data.type === 'MESSAGE_REACTION' && data.channelId && data.messageId) {
              const state = store.getState();
              const currentChannelId = state.chat.currentChannel?.id;
              const currentUserId = state.auth.user?.id;
              if (!currentUserId || String(data.reactorId) === String(currentUserId)) return;
              if (currentChannelId === data.channelId) return;

              const isDirectMessage = isDirectMessageChannelType(channel);
              notificationService.showReactionNotification({
                reactorName: data.reactorName,
                reactionLabel: data.reactionLabel || data.reactionEmoji,
                reactionId: data.reactionEmoji,
                messagePreview: data.messagePreview,
                channelName: channel.name || 'Channel',
                isDirectMessage,
                channelId: data.channelId,
                messageId: data.messageId,
                currentChannelId,
              });
              return;
            }

            // Handle messages for unread tracking and display
            if (data.id && data.senderId && data.channelId) {
              const state = store.getState();
              const currentChannelId = state.chat.currentChannel?.id;
              const currentUserId = state.auth.user?.id;
              const currentUser = state.auth.user;

              console.log('📨 Message received via global subscription:', {
                messageId: data.id,
                channelId: data.channelId,
                isCurrentChannel: currentChannelId === data.channelId,
                isFromCurrentUser: data.senderId === currentUserId,
              });

              const message = {
                id: data.id,
                clientMessageId: data.clientMessageId || null,
                tempId: data.clientMessageId || data.tempId || undefined,
                content: data.content || '',
                ...pickSensitiveMessageFields(data),
                ...pickChatTranslationFields(data),
                senderId: data.senderId,
                senderName: data.senderName,
                senderProfilePicture: data.senderProfilePicture || null,
                channelId: data.channelId,
                type: data.type,
                attachments: data.attachments || [],
                reactions: data.reactions || [],
                customTags: data.customTags || [],
                replyToMessageId: data.replyToMessageId || null,
                mentionedUserIds: data.mentionedUserIds || [],
                ...parseDeliveryFlagsFromPayload(data),
                isEdited: data.isEdited || false,
                linkPreview: data.linkPreview || null,
                meetingInfo: data.meetingInfo || null,
                appointmentInfo: data.appointmentInfo || null,
                eventInfo: data.eventInfo || null,
                attendanceInfo: data.attendanceInfo || null,
                notificationInfo: data.notificationInfo || null,
                workItemShare: data.workItemShare || null,
                linkedMeetingMessageId: data.linkedMeetingMessageId || null,
                workflowAssignedToUserId: data.workflowAssignedToUserId ?? null,
                workflowAssignedToName: data.workflowAssignedToName ?? null,
                workflowDescription: data.workflowDescription ?? null,
                workflowTitle: data.workflowTitle ?? null,
                workflowSpaceId: data.workflowSpaceId ?? null,
                workflowSpaceName: data.workflowSpaceName ?? null,
                workflowTrackId: data.workflowTrackId ?? null,
                workflowTrackLink: data.workflowTrackLink ?? null,
                templateInfo: data.templateInfo || null,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
                timestamp: data.timestamp || data.createdAt,
              };

              if (isAttendanceChannel(channel) && !isMyAttendanceTimelineMessage(message, currentUserId)) {
                return;
              }

              const existingMessages = state.chat.messages[data.channelId] || [];
              const existingMessage = existingMessages.find((m) => m.id === data.id);
              const ownReactionBroadcast = isOwnReactionBroadcast(existingMessage, message, currentUserId);

              dispatch(addMessage({
                channelId: data.channelId,
                message,
                currentUserId: currentUserId,
                skipUnreadCountIncrement: ownReactionBroadcast,
              }));
              dispatch(ingestLiveTrackingMessage(message));

              if (currentUserId && message.senderId !== currentUserId) {
                acknowledgeMessageDelivered(message, currentUserId);
              }

              // Blast popup for announcement templates that still need acknowledgement
              if (
                currentUserId
                && message.senderId !== currentUserId
                && message.templateInfo?.requiresAcknowledge
                && String(message.templateInfo?.templateType || '').toUpperCase() === 'ANNOUNCEMENT'
                && !(message.templateInfo?.acknowledgedByUserIds || []).includes(currentUserId)
              ) {
                window.dispatchEvent(new CustomEvent('workloop:template-announcement', {
                  detail: message,
                }));
              }

              // Show notification if not from current user AND not a reaction/read-only update
              if (shouldAnnounceIncomingMessage({
                existingMessage,
                incoming: message,
                currentUserId,
                currentChannelId,
                tabVisible: notificationService.isTabVisibleNow(),
                windowFocused: notificationService.isWindowFocusedNow(),
              })) {
                const isDirectMessage = isDirectMessageChannelType(channel);
                const channelName = channel.name || 'Channel';
                const isMentioned = isMessageMentioningUser(
                  message,
                  currentUserId,
                  currentUser?.username
                );

                if (isMentioned) {
                  dispatch(receiveMentionAlert({
                    channelId: data.channelId,
                    channelName,
                    isDirectMessage,
                    message,
                  }));
                  if (isHighlightedPopupEnabled()) {
                    dispatch(pushHighlightedToast(buildMessageHighlightToast(message, {
                      eventType: 'MENTION',
                      channelName,
                      isDirectMessage,
                    })));
                  }
                  notificationService.notifyInAppMention(
                    message,
                    channelName,
                    isDirectMessage,
                    currentChannelId,
                    currentUser
                  );
                } else {
                  notificationService.showMessageNotification(
                    message,
                    channelName,
                    isDirectMessage,
                    currentChannelId,
                    currentUser
                  );
                  if (isHighlightedPopupEnabled()) {
                    dispatch(pushHighlightedToast(buildMessageHighlightToast(message, {
                      eventType: 'MESSAGE',
                      channelName,
                      isDirectMessage,
                    })));
                  }
                }
              }
            }
          };

          websocketService.subscribeToChannel(channel.id, callback);
          activeCallbacks.set(channel.id, callback);
        }
      });
      websocketService.retryPendingSubscriptions();
    };

    runSubscribe();

    const onWsReady = () => {
      console.log('🔁 WebSocket ready — refreshing channel subscriptions');
      runSubscribe();
    };
    window.addEventListener('workloop:ws-connected', onWsReady);
    window.addEventListener('workloop:ws-reconnected', onWsReady);

    // Cleanup: Remove callbacks when channels change or component unmounts
    return () => {
      window.removeEventListener('workloop:ws-connected', onWsReady);
      window.removeEventListener('workloop:ws-reconnected', onWsReady);
      console.log(`🔌 Cleaning up ${activeCallbacks.size} WebSocket callbacks in ChatApp`);
      activeCallbacks.forEach((callback, channelId) => {
        websocketService.unsubscribeFromChannel(channelId, callback);
      });
      activeCallbacks.clear();
    };
  }, [channels, directMessages, dispatch]); // Removed currentChannel from dependencies

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleChannelSelect = (channel) => {
    dispatch(setCurrentChannel(channel));
    if (channel?.id) {
      dispatch(dismissHighlightedToastsByKeys([channel.id]));
      dispatch(markChannelAsRead(channel.id));
    }
    // Drop deep-link params so the next chat does not hunt a message from the previous one.
    setSearchParams((prev) => {
      if (!prev.get('messageId') && !prev.get('threadId') && !prev.get('channelId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('messageId');
      next.delete('threadId');
      next.delete('channelId');
      return next;
    }, { replace: true });
    setDailyTasksExpanded(false);
    setLiveTrackingOpen(false);
    if (isMobileLayout()) {
      dispatch(setSidebarOpen(false));
    }
  };

  const handleDailyTasksNavigate = async (item) => {
    if (!item?.messageId) return;
    const state = store.getState();
    const allChannels = [
      ...(state.channels?.channels || []),
      ...(state.channels?.directMessages || []),
    ];
    let found = await resolveLocalMessage(item.messageId, state.chat?.messages);
    let channelId = item.channelId || found?.channelId;
    if (!channelId) {
      const currentId = state.chat?.currentChannel?.id;
      const currentMsgs = currentId ? (state.chat?.messages?.[currentId] || []) : [];
      const foundHere = currentMsgs.find((m) => String(m.id || m._id) === String(item.messageId));
      channelId = foundHere?.channelId || currentId;
      if (foundHere) found = foundHere;
    }
    if (!found || !channelId) {
      try {
        const authToken = localStorage.getItem('token');
        if (!found && channelId) {
          const around = await axios.get(
            `${API_URL}/messages/channel/${channelId}/around/${item.messageId}`,
            {
              params: { before: 8, after: 2 },
              headers: { Authorization: authToken ? `Bearer ${authToken}` : '' },
            },
          );
          const aroundMsgs = around.data?.messages || [];
          found = aroundMsgs.find((msg) => String(msg.id) === String(item.messageId)) || found;
        }
        if (!channelId || !found) {
          const response = await axios.get(`${API_URL}/messages/${item.messageId}`, {
            headers: { Authorization: authToken ? `Bearer ${authToken}` : '' },
          });
          found = found || response.data;
          channelId = channelId || response.data?.channelId;
        }
      } catch {
        // keep whatever we already resolved
      }
    }
    if (!channelId) {
      dispatch(addNotification({ type: 'error', message: 'Channel not found.' }));
      return;
    }
    const targetChannel = allChannels.find((ch) => String(ch.id) === String(channelId));
    if (!targetChannel) {
      dispatch(addNotification({ type: 'error', message: 'Channel not found.' }));
      return;
    }
    dispatch(setCurrentChannel(targetChannel));
    setDailyTasksExpanded(false);
    setDailyTasksOpen(false);
    setLiveTrackingOpen(false);
    const channelMsgs = store.getState().chat?.messages?.[channelId] || [];
    const replyToMessageId = item.replyToMessageId || found?.replyToMessageId || null;
    let threadId = item.threadId || null;
    if (!threadId && replyToMessageId) {
      threadId = (found && getTopLevelParentId(found, channelMsgs)) || replyToMessageId;
    }
    const nextParams = { messageId: String(item.messageId), channelId: String(channelId) };
    if (threadId) {
      nextParams.threadId = String(threadId);
    }
    setSearchParams(nextParams);
    if (isMobileLayout()) {
      dispatch(setSidebarOpen(false));
    }
  };

  useEffect(() => {
    const handleGoToMessage = (event) => {
      handleDailyTasksNavigate(event?.detail || {});
    };
    window.addEventListener('workloop:go-to-message', handleGoToMessage);
    return () => window.removeEventListener('workloop:go-to-message', handleGoToMessage);
  }, []);

  useEffect(() => {
    const handleOpenNotification = async (event) => {
      const detail = event?.detail || {};
      const eventType = String(detail.eventType || '');
      if (eventType.toUpperCase() === 'LEAVE_APPROVAL_REQUEST') {
        window.dispatchEvent(new CustomEvent('workloop:open-team-attendance'));
        return;
      }
      const openAsTask = eventType.startsWith('TASK_')
        || (Boolean(detail.taskId) && !eventType.startsWith('WORKFLOW_'));

      if (openAsTask && detail.taskId) {
        try {
          const task = await dispatch(fetchTaskById(detail.taskId)).unwrap();
          if (task) {
            setNotificationWorkflow(null);
            setNotificationTask(task);
            return;
          }
        } catch (err) {
          dispatch(addNotification({
            type: 'error',
            message: typeof err === 'string' ? err : 'Could not open task',
          }));
          return;
        }
      }

      if (detail.sourceMessageId) {
        try {
          let found = await resolveLocalMessage(detail.sourceMessageId, store.getState().chat?.messages);
          if (!found && detail.sourceChannelId) {
            const token = localStorage.getItem('token');
            const around = await axios.get(
              `${API_URL}/messages/channel/${detail.sourceChannelId}/around/${detail.sourceMessageId}`,
              {
                params: { before: 2, after: 2 },
                headers: { Authorization: token ? `Bearer ${token}` : '' },
              },
            );
            found = (around.data?.messages || []).find(
              (msg) => String(msg.id) === String(detail.sourceMessageId),
            );
          }
          const channelId = detail.sourceChannelId || found?.channelId;
          const eventTypeUpper = String(eventType || '').toUpperCase();
          const openInChat = eventTypeUpper === 'MESSAGE_REACTION'
            || eventTypeUpper === 'MENTION'
            || eventTypeUpper === 'MESSAGE'
            || eventTypeUpper.startsWith('MEETING_');
          if (openInChat || !isWorkflowMessage(found)) {
            if (channelId) {
              window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
                detail: {
                  messageId: detail.sourceMessageId,
                  channelId,
                  replyToMessageId: found?.replyToMessageId || null,
                },
              }));
              return;
            }
          }
          const item = messageToWorkflowItem(found) || {
            messageId: detail.sourceMessageId,
            channelId,
          };
          if (item?.messageId && item?.channelId) {
            setNotificationTask(null);
            setNotificationWorkflow(item);
            return;
          }
        } catch (err) {
          dispatch(addNotification({
            type: 'error',
            message: typeof err === 'string' ? err : 'Could not open notification',
          }));
          return;
        }
      }

      if (detail.notificationChannelId && !user?.globalOrganization) {
        const inbox = (store.getState().channels?.channels || []).find(
          (c) => String(c.id) === String(detail.notificationChannelId)
        );
        if (inbox) {
          dispatch(setCurrentChannel(inbox));
          return;
        }
      }

      dispatch(addNotification({ type: 'error', message: 'Could not open this notification.' }));
    };
    window.addEventListener('workloop:open-notification', handleOpenNotification);
    return () => window.removeEventListener('workloop:open-notification', handleOpenNotification);
  }, [dispatch, user?.globalOrganization]);

  const handleExpandDailyTasks = () => {
    setCalendarOpen(false);
    setEventsOpen(false);
    setNotesOpen(false);
    setLiveTrackingOpen(false);
    setDailyTasksOpen(true);
    setDailyTasksExpanded(true);
    if (isMobileLayout()) {
      dispatch(setSidebarOpen(false));
    }
  };

  const handleCollapseDailyTasks = () => {
    setDailyTasksExpanded(false);
  };

  useEffect(() => {
    const intent = consumeSidebarIntent();
    if (!intent) return undefined;
    setCalendarOpen(false);
    setEventsOpen(false);
    setNotesOpen(false);
    setLiveTrackingOpen(false);
    if (intent === 'calendar') {
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setCalendarOpen(true);
      return undefined;
    }
    if (intent === 'events') {
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setEventsOpen(true);
      return undefined;
    }
    if (intent === 'notes') {
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setNotesOpen(true);
      return undefined;
    }
    if (intent === 'live-tracking') {
      setDailyTasksOpen(false);
      setDailyTasksExpanded(false);
      setLiveTrackingOpen(true);
      return undefined;
    }
    if (intent === 'daily-tasks') {
      handleExpandDailyTasks();
    }
    return undefined;
  }, [location.pathname]);

  const handleToggleDarkMode = () => {
    const newDarkMode = !darkMode;
    dispatch(toggleDarkMode());
    // Save dark mode preference to backend
    dispatch(updateDarkModePreference(newDarkMode));
  };

  // Fetch channels and direct messages on mount
  useEffect(() => {
    if (token && user && !isSuperAdmin) {
      dispatch(fetchChannels());
      dispatch(fetchDirectMessages());
      // Load lastViewed from backend
      dispatch(fetchLastViewed());
      dispatch(loadLiveTracking()).then(() => {
        dispatch(recomputeLiveTrackingAttention());
      });
    }
  }, [dispatch, token, user, isSuperAdmin]);

  // Fetch unread counts when channels/DMs are loaded (debounced — both lists update separately)
  useEffect(() => {
    if (isSuperAdmin) return undefined;
    if (!token) return undefined;

    const timer = setTimeout(() => {
      const { lastViewed } = store.getState().chat;
      const allChannels = [...channels, ...directMessages];
      const channelIds = allChannels.map((ch) => ch.id).filter(Boolean);
      if (channelIds.length > 0) {
        dispatch(fetchUnreadCounts({ channelIds, lastViewed }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [dispatch, channels, directMessages, token, isSuperAdmin]);

  // Update window title with unread count and signed-in name
  useEffect(() => {
    if (isSuperAdmin) return;

    const totalUnread = unreadCounts
      ? Object.values(unreadCounts).reduce((sum, count) => sum + (typeof count === 'number' ? count : 0), 0)
      : 0;
    applyWindowTitle(user, totalUnread);

    return () => {
      applyWindowTitle(user, 0);
    };
  }, [unreadCounts, isSuperAdmin, user]);

  // Polling mechanism to check for new messages every 30 seconds
  // This works alongside WebSocket as a backup/fallback mechanism
  // The addMessage reducer handles duplicates, so both can work simultaneously
  useEffect(() => {
    if (isSuperAdmin) return;
    if (!token || !user?.id) return;

    const checkForNewMessages = async () => {
      try {
        const state = store.getState();
        const fromStore = [...(state.channels?.channels || []), ...(state.channels?.directMessages || [])];
        let allChannels = fromStore;
        const currentChannelFromStore = state.chat.currentChannel;
        if (
          currentChannelFromStore?.id &&
          !allChannels.some((c) => c?.id === currentChannelFromStore.id)
        ) {
          allChannels = [...allChannels, currentChannelFromStore];
        }
        const currentChannelId = state.chat.currentChannel?.id;
        const currentUserId = state.auth.user?.id;
        const currentUser = state.auth.user;
        const existingMessages = state.chat.messages || {};
        const { lastViewed } = state.chat;
        const isWebSocketConnected = websocketService.isConnected();

        if (allChannels.length === 0) return;

        const channelIds = allChannels.map((ch) => ch.id).filter(Boolean);
        if (channelIds.length === 0) return;

        if (isRealmEnabled()) {
          await pullDelta();
          // Unread badges are handled by the dedicated unread poll below.
          await syncRecentChannelMessagesToRedux(channelIds, { limit: 15 });
          if (isWebSocketConnected) {
            return;
          }
          // WS disconnected: fall through to REST backfill below.
        }

        // WebSocket is primary for live messages. Only use the updates poll as a
        // missed-event fallback when the socket is down.
        if (isWebSocketConnected) {
          return;
        }

        console.log('🔄 Polling: WebSocket down — fetching message updates');

        // Keep badges fresh while recovering without WS.
        dispatch(fetchUnreadCounts({ channelIds, lastViewed }));

        const authToken = localStorage.getItem('token');
        const since = readMessageUpdatesCursor()
          || new Date(Date.now() - 2 * 60 * 1000).toISOString();
        const response = await axios.get(`${API_URL}/messages/updates`, {
          params: { since, limit: 100 },
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 15000,
        });
        const payload = response.data || {};
        if (payload.serverCursor) {
          writeMessageUpdatesCursor(payload.serverCursor);
        }

        const fetchedMessages = Array.isArray(payload.messages) ? payload.messages : [];
        if (!fetchedMessages.length) {
          return;
        }

        console.log(
          `📨 [Polling] ${fetchedMessages.length} update(s) across ${
            (payload.updatedChannelIds || []).length
          } channel(s)`
        );

        if (isRealmEnabled()) {
          upsertRealmDocs('Message', fetchedMessages).catch(() => {});
        }

        const channelsById = new Map(allChannels.map((ch) => [ch.id, ch]));

        fetchedMessages.forEach((msg) => {
          if (!msg?.id || !msg?.channelId) return;
          const channel = channelsById.get(msg.channelId) || { id: msg.channelId, type: null, name: 'Channel' };
          const message = mapPolledMessage(msg);
          const existingChannelMessages = existingMessages[msg.channelId] || [];
          const alreadyKnown = existingChannelMessages.some((m) => m.id === message.id);

          if (isAttendanceChannel(channel) && !isMyAttendanceTimelineMessage(message, currentUserId)) {
            return;
          }

          dispatch(
            addMessage({
              channelId: msg.channelId,
              message,
              currentUserId,
              skipUnreadCountIncrement: true,
            })
          );
          if (!alreadyKnown) {
            dispatch(ingestLiveTrackingMessage(message));
          }

          if (alreadyKnown) return;
          const existingBefore = existingChannelMessages.find((m) => m.id === message.id);
          if (!shouldAnnounceIncomingMessage({
            existingMessage: existingBefore,
            incoming: message,
            currentUserId,
            currentChannelId,
            tabVisible: notificationService.isTabVisibleNow(),
            windowFocused: notificationService.isWindowFocusedNow(),
          })) return;

          if (
            currentUserId
            && message.senderId !== currentUserId
            && message.templateInfo?.requiresAcknowledge
            && String(message.templateInfo?.templateType || '').toUpperCase() === 'ANNOUNCEMENT'
            && !(message.templateInfo?.acknowledgedByUserIds || []).includes(currentUserId)
          ) {
            window.dispatchEvent(new CustomEvent('workloop:template-announcement', {
              detail: message,
            }));
          }

          const isDirectMessage = isDirectMessageChannelType(channel);
          const channelName = channel.name || 'Channel';
          const isMentioned = isMessageMentioningUser(
            message,
            currentUserId,
            currentUser?.username
          );

          if (isMentioned) {
            dispatch(receiveMentionAlert({
              channelId: msg.channelId,
              channelName,
              isDirectMessage,
              message,
            }));
            if (isHighlightedPopupEnabled()) {
              dispatch(pushHighlightedToast(buildMessageHighlightToast(message, {
                eventType: 'MENTION',
                channelName,
                isDirectMessage,
              })));
            }
            notificationService.notifyInAppMention(
              message,
              channelName,
              isDirectMessage,
              currentChannelId,
              currentUser
            );
          } else {
            notificationService.showMessageNotification(
              message,
              channelName,
              isDirectMessage,
              currentChannelId,
              currentUser
            );
            if (isHighlightedPopupEnabled()) {
              dispatch(pushHighlightedToast(buildMessageHighlightToast(message, {
                eventType: 'MESSAGE',
                channelName,
                isDirectMessage,
              })));
            }
          }
        });
      } catch (error) {
        console.error('Error in polling check for new messages:', error);
      }
    };

    const initialTimeout = setTimeout(() => {
      checkForNewMessages();
    }, 2000);

    const pollIntervalMs = isRealmEnabled() ? 15000 : 30000;
    const pollingInterval = setInterval(() => {
      checkForNewMessages();
    }, pollIntervalMs);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(pollingInterval);
    };
  }, [dispatch, token, user?.id, isSuperAdmin]);

  // When tab/window gains focus (e.g. user returns after reading on mobile), refresh unread counts
  useEffect(() => {
    if (isSuperAdmin) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token) {
        const state = store.getState();
        const allChannels = [...(state.channels?.channels || []), ...(state.channels?.directMessages || [])];
        const channelIds = allChannels.map((ch) => ch?.id).filter(Boolean);
        if (channelIds.length > 0) {
          dispatch(fetchUnreadCounts({ channelIds, lastViewed: state.chat?.lastViewed }));
        }
        if (isRealmEnabled()) {
          pullDelta().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch, token, isSuperAdmin]);

  // Desktop: refresh from server when app window regains focus.
  useEffect(() => {
    if (!token || isSuperAdmin || !isRealmEnabled()) return undefined;
    const onFocus = () => {
      pullDelta().catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [token, isSuperAdmin]);

  // Faster unread-only poll (every 10s) when tab is visible
  // so badge updates quickly when user reads on another device.
  useEffect(() => {
    if (!token) return;
    if (isSuperAdmin) return;

    const refreshUnreadOnly = () => {
      if (document.visibilityState !== 'visible') return;
      const state = store.getState();
      const allChannels = [
        ...(state.channels?.channels || []),
        ...(state.channels?.directMessages || []),
      ];
      const channelIds = allChannels.map((ch) => ch?.id).filter(Boolean);
      if (channelIds.length > 0) {
        dispatch(
          fetchUnreadCounts({ channelIds, lastViewed: state.chat?.lastViewed })
        );
      }
    };

    const interval = setInterval(refreshUnreadOnly, 15000);
    // Channels-loaded effect already fetches once; avoid a second call at 2s.
    return () => {
      clearInterval(interval);
    };
  }, [dispatch, token, isSuperAdmin]);

  return (
    <>
      {isSuperAdmin ? null : (
        <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
          {/* Backdrop when sidebar is open on mobile */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => dispatch(toggleSidebar())}
              onKeyDown={(e) => e.key === 'Escape' && dispatch(toggleSidebar())}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
            />
          )}

          {/* Sidebar: fully collapsed (0 width) on mobile when closed; overlay when open on mobile */}
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sidebarOpen
                ? 'w-full lg:w-80 fixed lg:relative left-0 top-0 h-full z-40 lg:z-auto shadow-xl lg:shadow-none'
                : 'w-0 lg:w-16'
            }`}
          >            <Sidebar
              currentChannel={currentChannel}
              onChannelSelect={handleChannelSelect}
              onLogout={handleLogout}
              onToggleSidebar={() => dispatch(toggleSidebar())}
              onToggleDarkMode={handleToggleDarkMode}
              sidebarOpen={sidebarOpen}
              darkMode={darkMode}
              onDailyTasksToggle={() => {
                setCalendarOpen(false);
                setEventsOpen(false);
                setNotesOpen(false);
                setLiveTrackingOpen(false);
                if (taskPanelOpen) {
                  navigate('/chat');
                }
                if (dailyTasksExpanded) {
                  if (currentChannel) {
                    setDailyTasksExpanded(false);
                    setDailyTasksOpen(true);
                    if (isMobileLayout()) {
                      dispatch(setSidebarOpen(false));
                    }
                  }
                  return;
                }
                handleExpandDailyTasks();
              }}
              dailyTasksOpen={!currentChannel || dailyTasksExpanded || dailyTasksOpen}
              onCalendarToggle={() => {
                const nextOpen = !calendarOpen;
                setCalendarOpen(nextOpen);
                if (nextOpen) {
                  setEventsOpen(false);
                  setNotesOpen(false);
                  setLiveTrackingOpen(false);
                  setDailyTasksOpen(false);
                }
                if (nextOpen && window.innerWidth < 768) {
                  dispatch(setSidebarOpen(false));
                }
              }}
              calendarOpen={calendarOpen}
              onEventsToggle={() => {
                const nextOpen = !eventsOpen;
                setEventsOpen(nextOpen);
                if (nextOpen) {
                  setCalendarOpen(false);
                  setNotesOpen(false);
                  setLiveTrackingOpen(false);
                  setDailyTasksOpen(false);
                }
                if (nextOpen && window.innerWidth < 768) {
                  dispatch(setSidebarOpen(false));
                }
              }}
              eventsOpen={eventsOpen}
              onNotesToggle={() => {
                const nextOpen = !notesOpen;
                setNotesOpen(nextOpen);
                if (nextOpen) {
                  setCalendarOpen(false);
                  setEventsOpen(false);
                  setLiveTrackingOpen(false);
                  setDailyTasksOpen(false);
                }
                if (nextOpen && window.innerWidth < 768) {
                  dispatch(setSidebarOpen(false));
                }
              }}
              notesOpen={notesOpen}
              onLiveTrackingToggle={() => {
                const nextOpen = !liveTrackingOpen;
                setLiveTrackingOpen(nextOpen);
                if (nextOpen) {
                  setCalendarOpen(false);
                  setEventsOpen(false);
                  setNotesOpen(false);
                  setDailyTasksOpen(false);
                  setDailyTasksExpanded(false);
                }
                if (nextOpen && window.innerWidth < 768) {
                  dispatch(setSidebarOpen(false));
                }
              }}
              liveTrackingOpen={liveTrackingOpen}
            />
          </div>

          {/* Main Content Area: chat, or full-screen Today's Tasks */}
          <div className="flex-1 flex flex-row relative min-w-0 overflow-hidden">
            <div className="flex flex-col relative min-w-0 flex-1 overflow-hidden">
              {currentChannel && (
                <div className={`flex flex-col flex-1 min-h-0 overflow-hidden ${dailyTasksExpanded || liveTrackingOpen ? 'hidden' : ''}`}>
                  <ChatWindow
                    channel={currentChannel}
                    messageIdToScroll={messageIdToScroll}
                    threadIdToOpen={threadIdToOpen}
                    sidebarOpen={sidebarOpen}
                    onToggleSidebar={() => dispatch(toggleSidebar())}
                    dailyTasksOpen={dailyTasksOpen && !dailyTasksExpanded}
                    calendarOpen={calendarOpen}
                    onThreadStateChange={setThreadOpen}
                    setDailyTasksOpen={setDailyTasksOpen}
                    setCalendarOpen={setCalendarOpen}
                    onExpandDailyTasks={handleExpandDailyTasks}
                    onOpenUserWorkload={setWorkloadTargetUserId}
                    workloadTargetUserId={workloadTargetUserId}
                  />
                </div>
              )}
              {liveTrackingOpen && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <LiveTrackingPanel
                    onClose={() => {
                      setLiveTrackingOpen(false);
                      if (isMobileLayout()) {
                        dispatch(setSidebarOpen(true));
                      }
                    }}
                    headerLeading={
                      !sidebarOpen ? (
                        <button
                          type="button"
                          className="lg:hidden flex-shrink-0 inline-flex h-9 w-9 items-center justify-center -ml-1 rounded-xl border border-transparent bg-white/70 dark:bg-gray-800/70 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/60 transition-all"
                          onClick={() => dispatch(toggleSidebar())}
                          aria-label="Open menu"
                        >
                          <MenuIcon className="w-5 h-5" />
                        </button>
                      ) : null
                    }
                  />
                </div>
              )}
              {(!currentChannel || dailyTasksExpanded) && !liveTrackingOpen && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <DailyTasksPanel
                    homeLayout
                    isExpanded
                    onToggleExpand={currentChannel ? handleCollapseDailyTasks : undefined}
                    onClose={currentChannel ? () => {
                      setDailyTasksExpanded(false);
                      setDailyTasksOpen(false);
                      if (isMobileLayout()) {
                        dispatch(setSidebarOpen(true));
                      }
                    } : undefined}
                    onNavigateToMessage={handleDailyTasksNavigate}
                    initialViewingUserId={workloadTargetUserId}
                    onViewingUserChange={setWorkloadTargetUserId}
                    headerLeading={
                      !sidebarOpen ? (
                        <button
                          type="button"
                          className="lg:hidden flex-shrink-0 inline-flex h-9 w-9 items-center justify-center -ml-1 rounded-xl border border-transparent bg-white/70 dark:bg-gray-800/70 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/60 transition-all"
                          onClick={() => dispatch(toggleSidebar())}
                          aria-label="Open menu"
                        >
                          <MenuIcon className="w-5 h-5" />
                        </button>
                      ) : null
                    }
                  />
                </div>
              )}
            </div>

            {/* Task Management (nested route) — large modal over chat; chat stays mounted underneath */}
            {taskPanelOpen && (
              <div
                className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4"
                role="dialog"
                aria-modal="true"
                aria-label="Task management"
              >
                <button
                  type="button"
                  className="absolute inset-0 bg-black/50 dark:bg-black/60"
                  onClick={() => navigate('/chat')}
                  aria-label="Close task management"
                />
                <div
                  className="relative z-10 flex h-[min(92vh,calc(100vh-1.5rem))] w-[min(95vw,calc(100vw-1.5rem))] max-w-[1600px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                      {outlet || <TaskManagementPanel />}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Personal calendar from sidebar — full screen, separate from channel/DM calendar */}
          {eventsOpen && !taskPanelOpen && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Events"
            >
              <button
                type="button"
                className="absolute inset-0 bg-black/50 dark:bg-black/60"
                onClick={() => {
                  setEventsOpen(false);
                  setEventsFocusId(null);
                }}
                aria-label="Close events"
              />
              <div
                className="relative z-10 flex h-[min(96vh,calc(100vh-1rem))] w-[min(98vw,calc(100vw-1rem))] max-w-[960px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
                onClick={(e) => e.stopPropagation()}
              >
                <EventsHomePanel
                  openEventId={eventsFocusId}
                  onClose={() => {
                    setEventsOpen(false);
                    setEventsFocusId(null);
                    if (window.innerWidth < 768) {
                      dispatch(setSidebarOpen(true));
                    }
                  }}
                />
              </div>
            </div>
          )}

          {notesOpen && !taskPanelOpen && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Notes"
            >
              <div
                className="absolute inset-0 bg-black/50 dark:bg-black/60"
                onClick={() => setNotesOpen(false)}
                aria-hidden="true"
              />
              <div
                className="relative z-10 flex h-[min(92vh,840px)] w-[min(96vw,1280px)] flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.28)] dark:border-gray-700 dark:bg-gray-900"
                onClick={(e) => e.stopPropagation()}
              >
                <NotesHomePanel
                  onClose={() => {
                    setNotesOpen(false);
                    if (window.innerWidth < 768) {
                      dispatch(setSidebarOpen(true));
                    }
                  }}
                />
              </div>
            </div>
          )}

          {calendarOpen && !taskPanelOpen && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-label="My calendar"
            >
              <button
                type="button"
                className="absolute inset-0 bg-black/50 dark:bg-black/60"
                onClick={() => setCalendarOpen(false)}
                aria-label="Close calendar"
              />
              <div
                className="relative z-10 flex h-[min(96vh,calc(100vh-1rem))] w-[min(98vw,calc(100vw-1rem))] max-w-[1400px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
                onClick={(e) => e.stopPropagation()}
              >
                <CalendarPanel
                  personalMode
                  onClose={() => {
                    setCalendarOpen(false);
                    if (window.innerWidth < 768) {
                      dispatch(setSidebarOpen(true));
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Reminder triggered popup (WebSocket) */}
          {triggeredReminder && (
            <ReminderTriggeredModal
              reminder={
                Array.isArray(triggeredReminder.items) && triggeredReminder.items.length === 1
                  ? triggeredReminder.items[0]
                  : (!triggeredReminder.items ? triggeredReminder : null)
              }
              reminders={
                Array.isArray(triggeredReminder.items) && triggeredReminder.items.length > 1
                  ? triggeredReminder.items
                  : undefined
              }
              onClose={() => dispatch(clearTriggeredReminder())}
              onShowAll={() => {
                dispatch(clearTriggeredReminder());
                handleExpandDailyTasks();
                dispatch(focusPendingReminders());
              }}
              onSnooze={async (minutes, reminder) => {
                try {
                  await snoozeReminder(reminder, minutes);
                  dispatch(clearTriggeredReminder());
                  dispatch(addNotification({
                    type: 'success',
                    message: `Reminder snoozed for ${formatSnoozeDuration(minutes)}`,
                  }));
                } catch (err) {
                  dispatch(addNotification({
                    type: 'error',
                    message: err.response?.data?.error || err.message || 'Failed to snooze reminder',
                  }));
                }
              }}
              onComplete={async () => {
                const single = Array.isArray(triggeredReminder.items)
                  ? triggeredReminder.items[0]
                  : triggeredReminder;
                try {
                  await markReminderCompleteForToday(single);
                  dispatch(clearTriggeredReminder());
                  dispatch(addNotification({ type: 'success', message: 'Reminder completed for today' }));
                } catch (err) {
                  dispatch(addNotification({
                    type: 'error',
                    message: err.response?.data?.error || err.message || 'Failed to complete reminder',
                  }));
                }
              }}
              onSkip={async () => {
                const single = Array.isArray(triggeredReminder.items)
                  ? triggeredReminder.items[0]
                  : triggeredReminder;
                try {
                  await skipReminderForToday(single);
                  dispatch(clearTriggeredReminder());
                  dispatch(addNotification({ type: 'success', message: 'Reminder skipped for today (missed)' }));
                } catch (err) {
                  dispatch(addNotification({
                    type: 'error',
                    message: err.response?.data?.error || err.message || 'Failed to skip reminder',
                  }));
                }
              }}
              onToggleTask={async (task) => {
                const single = Array.isArray(triggeredReminder.items)
                  ? triggeredReminder.items[0]
                  : triggeredReminder;
                if (!task?.id || !single) return;
                try {
                  setTogglingReminderTask(true);
                  const updated = await toggleReminderTask(single, task.id);
                  if (updated) dispatch(patchTriggeredReminder(updated));
                } catch (err) {
                  dispatch(addNotification({
                    type: 'error',
                    message: err.response?.data?.error || err.message || 'Failed to update task',
                  }));
                } finally {
                  setTogglingReminderTask(false);
                }
              }}
              togglingTask={togglingReminderTask}
            />
          )}
          {reminderInvite && (
            <ReminderInviteModal
              reminder={reminderInvite}
              busy={inviteActionBusy}
              onAccept={async (invite) => {
                const inviteId = reminderInviteIdOf(invite);
                try {
                  setInviteActionBusy(true);
                  await acceptReminder(inviteId);
                  dispatch(addNotification({ type: 'success', message: 'Reminder accepted' }));
                  notifyRemindersForMeChanged();
                  window.dispatchEvent(new CustomEvent('workloop:reminders-flushed'));
                  const remaining = await fetchRemindersForMe();
                  const next = pickPendingReminderInvite(remaining, readReminderInviteLaterIds(user?.id), inviteId);
                  if (next) dispatch(setReminderInvite(next));
                  else dispatch(clearReminderInvite());
                } catch (err) {
                  dispatch(addNotification({
                    type: 'error',
                    message: err.response?.data?.error || err.message || 'Failed to accept reminder',
                  }));
                } finally {
                  setInviteActionBusy(false);
                }
              }}
              onReject={async (invite) => {
                const inviteId = reminderInviteIdOf(invite);
                try {
                  setInviteActionBusy(true);
                  await rejectReminder(inviteId);
                  dispatch(addNotification({ type: 'success', message: 'Reminder rejected' }));
                  notifyRemindersForMeChanged();
                  const remaining = await fetchRemindersForMe();
                  const next = pickPendingReminderInvite(remaining, readReminderInviteLaterIds(user?.id), inviteId);
                  if (next) dispatch(setReminderInvite(next));
                  else dispatch(clearReminderInvite());
                } catch (err) {
                  dispatch(addNotification({
                    type: 'error',
                    message: err.response?.data?.error || err.message || 'Failed to reject reminder',
                  }));
                } finally {
                  setInviteActionBusy(false);
                }
              }}
              onLater={async (invite) => {
                const inviteId = reminderInviteIdOf(invite);
                addReminderInviteLaterId(user?.id, inviteId);
                try {
                  const remaining = await fetchRemindersForMe();
                  const laterIds = readReminderInviteLaterIds(user?.id);
                  const next = pickPendingReminderInvite(remaining, laterIds, inviteId);
                  if (next) dispatch(setReminderInvite(next));
                  else dispatch(clearReminderInvite());
                } catch {
                  dispatch(clearReminderInvite());
                }
              }}
            />
          )}
          {notificationWorkflow && (
            <WorkflowViewDialog
              item={notificationWorkflow}
              onClose={() => setNotificationWorkflow(null)}
              onNavigateToChat={() => {
                const item = notificationWorkflow;
                setNotificationWorkflow(null);
                handleDailyTasksNavigate(item);
              }}
              onViewImage={(images, index) => setNotificationGallery({ images, index })}
              onDownload={async (attachment) => {
                try {
                  await downloadAttachment(attachment);
                } catch {
                  openAttachmentFallback(attachment);
                }
              }}
            />
          )}
          {notificationTask && (
            <TaskViewDialog
              task={notificationTask}
              onClose={() => setNotificationTask(null)}
              usePortal
              zIndexClass="z-[1000]"
              onNavigateToMessage={(task) => {
                setNotificationTask(null);
                handleDailyTasksNavigate({
                  messageId: task.messageId,
                  channelId: task.channelId,
                });
              }}
              onOpenWorkflow={(item) => {
                setNotificationTask(null);
                if (item?.messageId) setNotificationWorkflow(item);
              }}
            />
          )}
          {notificationGallery && (
            <ImageViewerModal
              images={notificationGallery.images}
              initialIndex={notificationGallery.index}
              onClose={() => setNotificationGallery(null)}
              onDownload={async (attachment) => {
                try {
                  await downloadAttachment(attachment);
                } catch {
                  openAttachmentFallback(attachment);
                }
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ChatApp;

